import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Button, Drawer, Flex } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { NumberInput, Select } from 'react-hook-form-mantine';

import {
	SECURITY_ALLOCATION_FORM_FIELD,
	useCreateSecurityAllocationDrawerController,
} from './useCreateSecurityAllocationDrawerController';
import { ProgrammeSecurityAllocationTct, SummaryMipNameTct } from '@dealstack/shared/lib/generated';

interface Props {
	opened: boolean;
	onClose: (createdTransactionId?: string) => void;
	onSuccess: () => void;
	allocation: ProgrammeSecurityAllocationTct | null;
	mips: SummaryMipNameTct[];
}

export const SecurityAllocationDrawer = ({
	opened,
	onClose,
	onSuccess,
	allocation,
	mips,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();

	const { allocationForm, onSubmit, isLoading, securityIssuedInCurrency, securityCurrency } =
		useCreateSecurityAllocationDrawerController(onClose, allocation, onSuccess);
	const allocationHeaderType = securityIssuedInCurrency ? securityCurrency : tCommonUi('units');

	useEffect(() => {
		if (opened) {
			allocationForm.reset();
		}
	}, [opened]);

	const mipDropdownOptions = useMemo(
		() =>
			mips.map((mip) => ({
				label: mip.programmeName,
				value: mip.id,
			})),
		[mips]
	);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				allocation
					? tCapManage('securities.details.allocations.drawer.update_title')
					: tCapManage('securities.details.allocations.drawer.create_title')
			}
		>
			{isLoading && <InlineLoader />}
			<FormProvider {...allocationForm}>
				<form style={{ height: '100%' }} onSubmit={allocationForm.handleSubmit(onSubmit())}>
					<Flex direction="column" gap="1rem" justify="space-between" h="100%">
						<Flex direction="column" gap="1rem">
							<Select
								name={SECURITY_ALLOCATION_FORM_FIELD.mipId}
								control={allocationForm.control}
								label={tCapManage(
									'securities.details.allocations.table.columns.mipName'
								)}
								placeholder={tCapManage(
									'securities.details.allocations.drawer.mipName.placeholder'
								)}
								size="md"
								rightSection={<Icon variant="caret-down" size="lg" />}
								rightSectionProps={{ style: { pointerEvents: 'none' } }}
								data={mipDropdownOptions}
							/>

							<NumberInput
								name={SECURITY_ALLOCATION_FORM_FIELD.allocation}
								control={allocationForm.control}
								size="md"
								hideControls={true}
								label={tCapManage(
									'securities.details.allocations.table.columns.allocation',
									{
										type: allocationHeaderType,
									}
								)}
								placeholder={tCapManage(
									'securities.details.allocations.drawer.allocation.placeholder'
								)}
								decimalScale={securityIssuedInCurrency ? 2 : undefined}
								style={{ flexGrow: 1 }}
							/>
						</Flex>

						<Button fullWidth mt="auto" type="submit">
							{allocation
								? tCapManage('securities.details.allocations.drawer.update_btn')
								: tCapManage('securities.details.allocations.drawer.create_btn')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
